import React, { useState, useEffect } from 'react';
import { 
  isTradingDay, 
  isWithinMarketHours
} from '../utils/tradingHours';

interface DateTimePickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (timestamp: number) => void;
  minDate?: Date;
  maxDate?: Date;
  initialDate?: Date;
  timezone?: string;
}

const DateTimePicker: React.FC<DateTimePickerProps> = ({
  isOpen,
  onClose,
  onSelect,
  minDate,
  maxDate = new Date(),
  initialDate,
  timezone = 'America/New_York'
}) => {
  const [selectedDate, setSelectedDate] = useState<Date>(
    initialDate ? new Date(initialDate) : new Date()
  );
  const [selectedHour, setSelectedHour] = useState<number>(9);
  const [selectedMinute, setSelectedMinute] = useState<number>(30);
  const [isPM, setIsPM] = useState<boolean>(false);
  const [validationError, setValidationError] = useState<string>('');

  useEffect(() => {
    if (isOpen && initialDate) {
      const date = new Date(initialDate);
      setSelectedDate(date);
      
      let hours = date.getHours();
      const minutes = date.getMinutes();
      const pm = hours >= 12;
      
      if (hours > 12) hours -= 12;
      if (hours === 0) hours = 12;
      
      setSelectedHour(hours);
      setSelectedMinute(minutes);
      setIsPM(pm);
    }
  }, [isOpen, initialDate]);

  if (!isOpen) return null;

  const validateAndSelect = () => {
    // Convert 12-hour to 24-hour format
    let hour24 = selectedHour;
    if (isPM && selectedHour !== 12) {
      hour24 += 12;
    } else if (!isPM && selectedHour === 12) {
      hour24 = 0;
    }

    // Get the date components from selectedDate
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    const day = selectedDate.getDate();

    // Create ISO date string for the selected date/time in the target timezone
    // Format: "YYYY-MM-DDTHH:mm:ss" then use Intl to get the correct UTC timestamp
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const timeStr = `${String(hour24).padStart(2, '0')}:${String(selectedMinute).padStart(2, '0')}:00`;
    
    // Use a more reliable method to convert timezone-aware datetime to UTC timestamp
    // Create a formatter that outputs in the target timezone
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
    
    // Create a test date and find the offset by comparing formatted vs actual time
    // This approach: We want to find a UTC timestamp such that when displayed in `timezone`, 
    // it shows the user's selected date/time
    
    // Start with a guess: treat the selected time as UTC
    const guessUtc = Date.UTC(year, month, day, hour24, selectedMinute, 0, 0);
    
    // Format this guess in the target timezone to see what it displays as
    const parts = formatter.formatToParts(new Date(guessUtc));
    const getPart = (type: string) => parts.find(p => p.type === type)?.value || '0';
    
    const displayedYear = parseInt(getPart('year'));
    const displayedMonth = parseInt(getPart('month')) - 1;
    const displayedDay = parseInt(getPart('day'));
    const displayedHour = parseInt(getPart('hour'));
    const displayedMinute = parseInt(getPart('minute'));
    
    // Calculate the difference between what we want and what we got
    const displayedUtc = Date.UTC(displayedYear, displayedMonth, displayedDay, displayedHour, displayedMinute, 0, 0);
    const wantedUtc = Date.UTC(year, month, day, hour24, selectedMinute, 0, 0);
    const offsetMs = displayedUtc - wantedUtc;
    
    // Adjust the timestamp by the offset to get the correct UTC time
    const datetime = new Date(guessUtc - offsetMs);

    // Debug logging
    console.log('[DateTimePicker] Validating:', {
      dateStr,
      timeStr,
      timezone,
      wantedLocal: `${dateStr} ${timeStr}`,
      resultUtc: datetime.toISOString(),
      resultInTz: datetime.toLocaleString('en-US', { timeZone: timezone }),
      offsetMs,
      hour24,
      selectedMinute
    });

    // Validate trading day (using selected timezone)
    if (!isTradingDay(datetime, timezone)) {
      console.log('[DateTimePicker] Failed trading day check for timezone:', timezone);
      setValidationError('Selected date is not a trading day (weekend or holiday)');
      return;
    }

    // Validate market hours (9:30 AM - 4:00 PM in selected timezone)
    if (!isWithinMarketHours(datetime, timezone)) {
      setValidationError(`Selected time is outside market hours (9:30 AM - 4:00 PM ${timezone === 'America/New_York' ? 'EST' : timezone})`);
      return;
    }

    // Validate against min/max dates
    if (minDate && datetime < minDate) {
      setValidationError('Selected date is before the minimum allowed date');
      return;
    }

    if (maxDate && datetime > maxDate) {
      setValidationError('Selected date is after the maximum allowed date');
      return;
    }

    // All validations passed
    setValidationError('');
    onSelect(datetime.getTime());
    onClose();
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Parse date string as local timezone (not UTC)
    const [year, month, day] = e.target.value.split('-').map(Number);
    const newDate = new Date(year, month - 1, day, 12, 0, 0); // Set to noon to avoid timezone issues
    setSelectedDate(newDate);
    setValidationError('');
  };

  // Generate hour options (1-12)
  const hours = Array.from({ length: 12 }, (_, i) => i + 1);
  
  // Generate minute options (0, 15, 30, 45)
  const minutes = [0, 15, 30, 45];

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      {/* Semi-transparent backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Picker Modal */}
      <div className="relative bg-gradient-to-b from-slate-950/95 to-slate-900/95 backdrop-blur-md border border-slate-700/80 rounded-t-2xl sm:rounded-2xl shadow-2xl p-2 sm:p-6 max-w-md w-full mx-2 sm:mx-4 animate-fade-in-up">
        {/* Header */}
        <div className="flex items-center justify-between mb-2 sm:mb-4">
          <h3 className="text-[11px] sm:text-base font-semibold text-slate-100">Select Replay Start Date and Time</h3>
          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-200 hover:bg-slate-800 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Date & Time Selection */}
        <div className="mb-2 sm:mb-4">
          <div className="flex items-center gap-1.5 sm:gap-2 flex-nowrap">
            <input
              type="date"
              value={selectedDate && !isNaN(selectedDate.getTime()) ? selectedDate.toISOString().split('T')[0] : ''}
              onChange={handleDateChange}
              max={maxDate && !isNaN(maxDate.getTime()) ? maxDate.toISOString().split('T')[0] : undefined}
              min={minDate && !isNaN(minDate.getTime()) ? minDate.toISOString().split('T')[0] : undefined}
              className="w-[108px] sm:flex-1 px-2 sm:px-3 py-1 sm:py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 text-[11px] sm:text-sm focus:outline-none focus:border-blue-500/60 focus:ring-1 focus:ring-blue-500/30 transition-colors"
            />

            <select
              value={selectedHour}
              onChange={(e) => setSelectedHour(Number(e.target.value))}
              className="w-11 sm:w-16 px-2 py-1 sm:py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 text-[11px] sm:text-sm focus:outline-none focus:border-blue-500/60 focus:ring-1 focus:ring-blue-500/30 transition-colors"
              aria-label="Hour"
            >
              {hours.map(h => (
                <option key={h} value={h}>{h}</option>
              ))}
            </select>

            <span className="text-slate-400 font-bold flex-shrink-0">:</span>

            <select
              value={selectedMinute}
              onChange={(e) => setSelectedMinute(Number(e.target.value))}
              className="w-11 sm:w-16 px-2 py-1 sm:py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 text-[11px] sm:text-sm focus:outline-none focus:border-blue-500/60 focus:ring-1 focus:ring-blue-500/30 transition-colors"
              aria-label="Minute"
            >
              {minutes.map(m => (
                <option key={m} value={m}>{m.toString().padStart(2, '0')}</option>
              ))}
            </select>

            <div className="flex bg-slate-900/50 border border-slate-700 rounded-lg overflow-hidden flex-shrink-0">
              <button
                onClick={() => setIsPM(false)}
                className={`px-2 sm:px-3 py-1 sm:py-2 text-[11px] sm:text-xs font-semibold transition-colors ${
                  !isPM ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white' : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                AM
              </button>
              <button
                onClick={() => setIsPM(true)}
                className={`px-2 sm:px-3 py-1 sm:py-2 text-[11px] sm:text-xs font-semibold transition-colors ${
                  isPM ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white' : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                PM
              </button>
            </div>
          </div>

        </div>

        {/* Validation Error */}
        {validationError && (
          <div className="mb-3 p-3 bg-rose-500/10 border border-rose-500/30 rounded-lg text-sm text-rose-300">
            {validationError}
          </div>
        )}

        {/* Action Buttons */}
        <div>
          <button
            onClick={validateAndSelect}
            className="w-full px-3 sm:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 rounded-lg text-white text-xs sm:text-sm font-semibold transition-colors shadow-lg shadow-blue-500/20"
          >
            Start Replay
          </button>
        </div>

        {/* Info Footer */}
        <div className="hidden sm:block mt-4 pt-4 border-t border-slate-800">
          <p className="text-xs text-slate-500 text-center">
            💡 You can still click on the chart to select a different starting point
          </p>
        </div>
      </div>
    </div>
  );
};

export default DateTimePicker;
